/**
 * O descarte de menção na tela de menções: o dono do "tem certeza?".
 *
 * A confirmação voltou por decisão da dona em 2026-10-18. Em 2026-09-21
 * ela tinha saído, porque era a queixa; agora existe a lixeira (a menção
 * descartada fica 15 dias e pode ser restaurada) e o pedido explícito de
 * confirmar antes. Sem campo de motivo. O toast com "Desfazer" continua,
 * porque custa nada. Restaurar NÃO confirma: não é destrutivo. O lote
 * confirma SEMPRE, porque não tem desfazer.
 */
import { useCallback, useRef, useState } from "react";
import {
  apiClient,
  type BulkEdit,
  type BulkTarget,
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import type { Mention } from "@/shared/types/mention";

export type DiscardRequest =
  | { kind: "single"; mention: Mention }
  | { kind: "bulk"; count: number | null };

type ToastValue = { message: string; undo?: () => void };

/** Título e texto do diálogo de confirmação. */
export function discardDialogCopy(request: DiscardRequest): {
  title: string;
  description: string;
} {
  if (request.kind === "single") {
    return {
      title: "Descartar esta menção?",
      description:
        "Ela sai da lista, do resumo e das amostras geradas daqui em diante. Fica na lixeira por 15 dias e pode ser restaurada.",
    };
  }
  const { count } = request;
  const title =
    count === null
      ? "Descartar todas as menções do filtro?"
      : count === 1
        ? "Descartar 1 menção?"
        : `Descartar ${count.toLocaleString("pt-BR")} menções?`;
  return {
    title,
    description:
      "Elas saem da lista, do resumo e das amostras geradas daqui em diante. Ficam na lixeira por 15 dias e podem ser restauradas — o servidor congela a lista no clique.",
  };
}

type Options = {
  token: string;
  /** A página passa `() => void load()`. */
  reload: () => void;
  /** A página passa `setToast`. */
  notify: (toast: ToastValue | null) => void;
  bulkTarget: () => BulkTarget;
  onBulkSubmitted: (result: BulkEdit) => void;
};

export function useDiscardActions(options: Options) {
  // Lido por ref: as funções abaixo mantêm identidade estável e ainda
  // enxergam o token, o alvo e o recorte da renderização mais recente.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [pending, setPending] = useState<DiscardRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const discardWithUndo = useCallback(async (mention: Mention) => {
    const { token, reload, notify } = optionsRef.current;
    await apiClient.discardMention(token, mention.id);
    reload();
    notify({
      message: "Menção descartada.",
      undo: async () => {
        const current = optionsRef.current;
        current.notify(null);
        try {
          await apiClient.restoreMention(current.token, mention.id);
        } catch (failure) {
          current.notify({
            message: errorMessage(failure, "Não foi possível desfazer."),
          });
          return;
        }
        current.reload();
      },
    });
  }, []);

  const restoreWithUndo = useCallback(async (mention: Mention) => {
    const { token, reload, notify } = optionsRef.current;
    try {
      await apiClient.restoreMention(token, mention.id);
    } catch (failure) {
      notify({
        message: errorMessage(failure, "Não foi possível restaurar a menção."),
      });
      return;
    }
    reload();
    notify({
      message: "Menção restaurada.",
      undo: async () => {
        const current = optionsRef.current;
        current.notify(null);
        try {
          await apiClient.discardMention(current.token, mention.id);
        } catch (failure) {
          current.notify({
            message: errorMessage(failure, "Não foi possível desfazer."),
          });
          return;
        }
        current.reload();
      },
    });
  }, []);

  const requestDiscard = useCallback(
    (mention: Mention) => {
      if (mention.discardedAt !== null) {
        void restoreWithUndo(mention);
        return;
      }
      setError(null);
      setPending({ kind: "single", mention });
    },
    [restoreWithUndo],
  );

  const requestBulkDiscard = useCallback((count: number | null) => {
    setError(null);
    setPending({ kind: "bulk", count });
  }, []);

  const restoreBulk = useCallback(async () => {
    const current = optionsRef.current;
    try {
      const result = await apiClient.submitBulkClassification(
        current.token,
        current.bulkTarget(),
        { discarded: { value: false } },
      );
      current.onBulkSubmitted(result);
    } catch (failure) {
      current.notify({
        message: errorMessage(failure, "Não foi possível restaurar as menções."),
      });
    }
  }, []);

  const cancel = useCallback(() => {
    setPending(null);
    setError(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      if (pending.kind === "single") {
        await discardWithUndo(pending.mention);
      } else {
        const current = optionsRef.current;
        const result = await apiClient.submitBulkClassification(
          current.token,
          current.bulkTarget(),
          { discarded: { value: true } },
        );
        current.onBulkSubmitted(result);
      }
      setPending(null);
    } catch (failure) {
      setError(
        errorMessage(
          failure,
          pending.kind === "single"
            ? "Não foi possível descartar a menção."
            : "Não foi possível descartar as menções.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }, [pending, discardWithUndo]);

  return {
    pending,
    busy,
    error,
    requestDiscard,
    requestBulkDiscard,
    restoreBulk,
    confirm,
    cancel,
  };
}
